import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { REMOTE_FEATURE_LABELS } from '@ids-constants';
import { TargetDeviceService } from '@ids-services';
import { FormBuilderComponent } from '@microsec/components';
import { SAVE_CHANGES_LABEL, VALIDATOR_TYPE } from '@microsec/constants';
import { FormItem } from '@microsec/models';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';

const FORM_PARAMS = {
  SCAN_TRIGGER: 'config_active_scan_trigger_type',
  SCAN_MODE: 'config_active_scan_mode',
  INTERFACES: 'config_active_scan_interfaces',
  FREQUENCY: 'config_active_scan_period',
  IP_RANGES: 'config_active_ip_include_range',
  EXCLUDE_IP_RANGES: 'config_active_ip_exclude_range',
};

const SCAN_TRIGGER_VALUES = {
  MANUAL: 'manual',
  PERIODIC: 'periodic',
};

const SCAN_TRIGGER_OPTIONS = [
  { label: 'Manual', value: SCAN_TRIGGER_VALUES.MANUAL },
  { label: 'Periodic', value: SCAN_TRIGGER_VALUES.PERIODIC },
];

const IPV4_ADDRESS_OR_RANGE_PATTERN =
  '^(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)(?:/(3[0-2]|[1-2]?[0-9]))?$';

@Component({
  selector: 'app-microagent-config-active-scan-form',
  templateUrl: './microagent-config-active-scan-form.component.html',
  styleUrls: ['./microagent-config-active-scan-form.component.scss'],
  imports: [FormBuilderComponent],
})
export class MicroagentConfigActiveScanFormComponent extends BaseComponent implements AfterViewInit {
  showAdvancedSettings = false;

  deviceId: any = null;

  configuration: any = null;

  feature: any = null;

  fields: FormItem[] = [];

  interfaceOptions: any[] = [];

  @ViewChild('fb') form!: FormBuilderComponent;

  FORM_PARAMS = FORM_PARAMS;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  constructor(
    private targetDeviceService: TargetDeviceService,
    public dialogConfig: DynamicDialogConfig,
  ) {
    super();
  }

  async ngAfterViewInit() {
    await this.prepareConfigs();
    this.deviceId = this.dialogConfig?.data?.deviceId;
    this.configuration = this.dialogConfig?.data?.configuration;
    this.feature = this.dialogConfig?.data?.feature;
    this.interfaceOptions = this.dialogConfig?.data?.interfaceOptions;
    this.initForm();
  }

  initForm() {
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        label: 'Scanning Settings',
        labelStyleClass: 'font-bold',
        field: 'text',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.SCAN_TRIGGER,
        label: 'Scan Trigger',
        options: [...SCAN_TRIGGER_OPTIONS],
        required: true,
        field: 'radio',
        layout: 'horizontal',
        fieldInfo: 'Trigger the scans manually or periodically',
        defaultValue: SCAN_TRIGGER_VALUES.MANUAL,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.SCAN_MODE,
        label: 'Scan Mode',
        options: [
          { label: 'Safe (default)', value: 'safe' },
          { label: 'Advanced', value: 'advanced' },
        ],
        required: true,
        field: 'radio',
        layout: 'horizontal',
        fieldInfo: 'Advanced Mode provides a more detailed scan, but may have higher impact on the device',
        defaultValue: 'safe',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.FREQUENCY,
        label: 'Scan Frequency (s)',
        required: true,
        field: 'number',
        fieldInfo: 'How often the periodic scans are triggered automatically',
        minValue: 0,
        defaultValue: 1000,
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.IP_RANGES,
        label: 'Select IP Ranges or Addresses',
        field: 'input-chip',
        regex: IPV4_ADDRESS_OR_RANGE_PATTERN,
        fieldInfo:
          'Enter the IP address or IP range. For IP range, indicate the subnet mask in CIDR notation (e.g. /25). For example, enter 11.1.15.0/25 for IP range from 11.1.15.0 to 11.1.15.127.',
        defaultValue: [],
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.EXCLUDE_IP_RANGES,
        label: 'IP Ranges or Addresses to Exclude',
        field: 'input-chip',
        regex: IPV4_ADDRESS_OR_RANGE_PATTERN,
        fieldInfo:
          'Enter the IP address or IP range. For IP range, indicate the subnet mask in CIDR notation (e.g. /25). For example, enter 11.1.15.0/25 for IP range from 11.1.15.0 to 11.1.15.127.',
        defaultValue: [],
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.INTERFACES,
        label: 'Interface(s)',
        required: true,
        field: 'multiselect',
        options: this.interfaceOptions,
        fieldInfo: 'Only the selected interface(s) will be scanned',
        placeholder: 'Select interface(s) on this device',
        defaultValue: [],
      } as FormItem),
    ];
    this.fields = fields;
    this.form?.patchValue({
      ...this.configuration?.[this.feature],
    });
    setTimeout(() => {
      this.form?.setChangeEvent(FORM_PARAMS.SCAN_TRIGGER, (value) => {
        this.form?.setControlValidatorsAndVisibility(FORM_PARAMS.FREQUENCY, value === SCAN_TRIGGER_VALUES.PERIODIC ? [VALIDATOR_TYPE.REQUIRED] : []);
      });
    });
  }

  onSubmit(closeDialog: () => void) {
    this.form.isLoading = true;
    const formData = this.form.getRawValue();
    const scanTrigger = this.form.getControlValue(FORM_PARAMS.SCAN_TRIGGER);

    const payload = {
      configuration: {
        [this.feature]: {
          ...formData,
        },
      },
    };

    if (scanTrigger === SCAN_TRIGGER_VALUES.MANUAL) {
      delete payload.configuration[this.feature]?.[FORM_PARAMS.FREQUENCY];
    }

    this.targetDeviceService
      .updateDeviceConfiguration(this.deviceId, payload)
      .pipe(
        finalize(() => {
          this.form.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(`${REMOTE_FEATURE_LABELS[this.feature] || this.feature} Configuration Updated Successfully`);
          closeDialog();
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }
}
